<template>
  <b-modal
    ref="reisetermin-anlegen-modal"
    id="modalReiseterminAnlegen"
    title="Reisetermin anlegen"
    class="modalReiseterminAnlegen"
    centered
    @show="onShow"
    @hide="onHide"
  >
    <b-overlay :show="isLoading">
      <div class="d-flex align-items-center justify-content-between">
        <b-form-group label="Reisekürzel" label-for="reisekuerzel" class="mb-6">
          <AppSelect
            v-model="$v.form.reisekuerzel.$model"
            :options="reisenOptions"
            label="Reisekürzel"
            placeholder="Suchen..."
            :style="{
              '--app-select-border-color': $v.form.reisekuerzel.$error ? '#E5EAEE' : '#1BC5BD',
              width: '200px',
            }"
          />
        </b-form-group>
        <b-dropdown
          v-if="form.reisekuerzel && !isSerie"
          text="Termine ins nächste Jahr kopieren"
          style="max-width: 280px; height: 38px"
        >
          <b-dropdown-item
            @click="loadReiseterminStartdatenToNextYear(2024)"
            id="loadReiseterminStartdatenToNextYear2024"
          >
            2024
          </b-dropdown-item>
          <b-dropdown-item
            @click="loadReiseterminStartdatenToNextYear(2025)"
            id="loadReiseterminStartdatenToNextYear2025"
          >
            2025
          </b-dropdown-item>
          <b-dropdown-item
            @click="loadReiseterminStartdatenToNextYear(2026)"
            id="loadReiseterminStartdatenToNextYear2026"
          >
            2026
          </b-dropdown-item>
          <b-dropdown-item
            @click="loadReiseterminStartdatenToNextYear(2027)"
            id="loadReiseterminStartdatenToNextYear2027"
          >
            2027
          </b-dropdown-item>
          <b-dropdown-item
            @click="loadReiseterminStartdatenToNextYear(2028)"
            id="loadReiseterminStartdatenToNextYear2028"
          >
            2028
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div class="d-flex" style="gap: 8px">
        <b-button
          size="sm"
          :class="!isSerie && !isDatenUpload ? 'dialog-btn' : 'dialog-btn-secondary'"
          :variant="!isSerie && !isDatenUpload ? 'primary' : 'secondary'"
          @click="
            isSerie = false;
            isDatenUpload = false;
          "
          style="width: 130px"
        >
          Termin anlegen
        </b-button>
        <b-button
          size="sm"
          :class="isSerie && !isDatenUpload ? 'dialog-btn' : 'dialog-btn-secondary'"
          :variant="isSerie && !isDatenUpload ? 'primary' : 'secondary'"
          @click="
            isSerie = true;
            isDatenUpload = false;
          "
          style="width: 130px"
        >
          Serie anlegen
        </b-button>
        <b-button
          size="sm"
          :class="isDatenUpload ? 'dialog-btn' : 'dialog-btn-secondary'"
          :variant="isDatenUpload ? 'primary' : 'secondary'"
          @click="
            isDatenUpload = true;
            isSerie = false;
          "
          style="width: 130px; margin-left: auto"
        >
          Daten-Upload
        </b-button>
      </div>
      <div
        v-if="isDatenUpload"
        class="mt-4 p-3"
        style="border: 1px solid #e8e8e8; border-radius: 1px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1)"
      >
        <div class="mb-3">Bitte lade die Daten als CSV-Datei (.csv) hoch, um sie zu verarbeiten.</div>
        <b-input-group style="width: 100%">
          <b-input-group-prepend is-text id="foo">
            <i class="fas fa-file-csv"></i>
          </b-input-group-prepend>
          <b-form-file
            ref="formFileUpload"
            id="form-csv-File"
            accept=".csv"
            :value="null"
            :multiple="false"
            placeholder="Wähle eine CSV-Datei aus"
            @change="uploadCSV"
            browse-text="Datei auswählen"
          ></b-form-file>
        </b-input-group>
        <div class="mt-3 text-danger" v-if="csvFehlermeldung">{{ csvFehlermeldung }}</div>
      </div>
      <div class="d-flex mb-4 mt-2" style="flex-direction: row; gap: 6px; flex-wrap: wrap">
        <b-button
          v-if="!isSerie && isTerminAlreadyExistantSoloTermine"
          :disabled="!isTerminAlreadyExistantSoloTermine"
          @click="removeAlreadyExistingDatesSolo"
          class="dialog-btn"
          variant="danger"
          size="sm"
        >
          Entferne existierende Daten
        </b-button>
        <!-- Für den csv-Upload und ins nächste Jahr kopieren -->
        <b-button
          v-if="!isSerie && istGesperrterTerminVorhandenSolo"
          @click="removeGesperrteDatesSolo"
          class="dialog-btn"
          variant="danger"
          size="sm"
        >
          Entferne gesperrte Daten
        </b-button>
      </div>
      <b-form-group v-if="!isSerie && !isDatenUpload" label-for="date-picker" class="mb-0">
        <template #label>
          <div class="d-flex align-items-center">
            <span>Abreisedatum</span>
            <div class="bg-primary plus-sign ml-2" @click="addAbreisedatum">+</div>
          </div>
        </template>
        <div style="display: flex; flex-direction: column; gap: 8px">
          <div
            v-for="(abreisedatum, i) in abreisedaten"
            :key="'abreisedatum-' + i"
            class="d-flex"
            style="gap: 8px"
          >
            <RestriktionenDatePicker
              v-model="abreisedaten[i].value"
              :restriktionen-by-day="restriktionenByDay"
              :disabled-date="disabledBeforeToday"
              :default-value="lastAbreisedatum"
              placeholder="Wähle ein Datum..."
              style="width: 200px"
              :class="inputStatusKlasse(abreisedatum.value)"
            ></RestriktionenDatePicker>
            <i
              v-if="i > 0"
              class="fas fa-times text-danger align-self-center"
              @click="removeAbreisedatum(abreisedatum.id)"
              style="cursor: pointer"
            ></i>
          </div>
        </div>
        <div class="d-flex">
          <div class="ml-auto" v-if="form.reisekuerzel && abreisedaten.length > 0">
            {{ abreisedaten?.filter(item => item.value).length }} Reisetermine (Gesamt:
            {{ (existingDates?.length ?? 0) + abreisedaten.length }})
          </div>
        </div>
      </b-form-group>
      <div v-if="isSerie">
        <div class="d-flex" style="gap: 8px">
          <b-form-group class="w-50" label="Beginn" label-for="beginn">
            <RestriktionenDatePicker
              v-model="$v.form.beginnSerie.$model"
              :restriktionen-by-day="restriktionenByDay"
              :disabled-date="disabledBeforeToday"
              placeholder="Wähle ein Startdatum..."
              class="w-100"
            ></RestriktionenDatePicker>
          </b-form-group>
          <b-form-group class="w-50" label="Ende" label-for="ende">
            <RestriktionenDatePicker
              v-model="$v.form.endeSerie.$model"
              :restriktionen-by-day="restriktionenByDay"
              :disabled-date="disabledBeforeToday"
              :default-value="form.beginnSerie ?? new Date()"
              placeholder="Wähle ein Enddatum..."
              class="w-100"
            ></RestriktionenDatePicker>
          </b-form-group>
        </div>

        <b-form-group class="mb-4">
          <div class="d-flex">
            <div>Serienmuster:</div>
          </div>
          <div class="p-4" style="background-color: #e0e0e0; border-radius: 12px">
            <span class="d-flex align-items-center">
              Jede/Alle
              <b-form-input
                v-model="everyXWeek"
                style="width: 40px; height: 24px"
                class="ml-2 mr-2"
              ></b-form-input>
              Woche(n) am:
            </span>
            <b-form-checkbox-group
              v-model="selectedDays"
              :options="dayOptions"
              value-field="item"
              text-field="name"
              class="mt-2"
              style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px"
            ></b-form-checkbox-group>
          </div>
        </b-form-group>
        <div class="my-3" v-if="form.reisekuerzel && formatDatesInInterval?.length > 0">
          {{ formatDatesInInterval?.length }} Reisetermine (Gesamt:
          {{ (existingDates?.length ?? 0) + (formatDatesInInterval?.length ?? 0) }})
        </div>
      </div>
      <b-button
        v-if="isSerie && isTerminAlreadyExistantInSerie"
        :disabled="!isTerminAlreadyExistantInSerie"
        @click="removeAlreadyExistingDatesSerie"
        variant="danger"
        size="sm"
        class="mb-4"
      >
        Entferne existierende Daten
      </b-button>
      <div
        v-if="isSerie && formatDatesInInterval.length > 0"
        style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px"
        class="mb-4"
      >
        <div
          v-for="date in formatDatesInInterval"
          :key="date"
          class="d-flex"
          :class="isFormattedDateInExistingDates(date) ? 'existingReisetermin' : 'bgLightGray'"
        >
          <span :id="date" class="p-1">
            {{ date }}
          </span>

          <i
            class="fas fa-times text-danger align-self-center ml-auto mr-2"
            @click="removeDateFromInterval(date)"
            style="cursor: pointer"
          ></i>
          <b-tooltip
            :target="date"
            triggers="hover"
            custom-class="custom-tooltip"
            v-if="isFormattedDateInExistingDates(date)"
          >
            Reisetermin existiert bereits!
          </b-tooltip>
        </div>
      </div>
    </b-overlay>

    <template #modal-footer="{ hide }" class="pl-1">
      <b-button class="dialog-btn-secondary" @click="hide">Abbrechen</b-button>
      <b-button
        class="dialog-btn"
        :disabled="disableSichernButton"
        variant="primary"
        @click="reiseterminAnlegen(hide)"
      >
        Erstellen
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { validationMixin } from 'vuelidate';
import AppSelect from '@/components/common/app-select/app-select.vue';
import { required } from 'vuelidate/lib/validators';
import { exactlySixChars, noDigits } from '@/core/common/helpers/utils.js';
import apiService from '@/core/common/services/api.service';
import { mapState } from 'vuex';
import buildQuery from 'odata-query';

import {
  addDays,
  getDay,
  isAfter,
  getWeek,
  format,
  parse,
  compareAsc,
  setDay,
  setYear,
  getYear,
  startOfYear,
  isEqual,
  endOfYear,
  isSameDay,
} from 'date-fns';
import moment from 'moment-timezone';
import RestriktionenDatePicker from '@/components/restriktionen/restriktionen-date-picker.vue';
import {
  toDayKey,
  groupRestriktionenByDay,
  mapKonfigurationToRestriktionen,
  DAY_KEY_FORMAT,
} from '@/components/restriktionen/restriktionen-calendar.helper.js';
import { sortRestriktionenByPriority } from '@/components/restriktionen/restriktionen.constants.js';
import { getRestriktionskonfiguration } from '@/core/restriktionen/services/restriktionskonfiguration.service.js';

export default {
  mixins: [validationMixin],
  name: 'ReiseterminAnlegenModal',
  components: { AppSelect, RestriktionenDatePicker },
  watch: {
    'form.beginnSerie': 'setDatesInInterval',
    'form.endeSerie': 'setDatesInInterval',
    everyXWeek: 'setDatesInInterval',
    selectedDays: 'setDatesInInterval',
    restriktionenByDay: 'setDatesInInterval',
    'form.reisekuerzel': 'loadReisekuerzelInformation',
    isSerie() {
      this.abreisedaten = [{ id: crypto.randomUUID(), value: null }];
      this.form.beginnSerie = null;
      this.form.endeSerie = null;
    },
    isDatenUpload(newVal) {
      if (newVal === true) {
        this.abreisedaten = [{ id: crypto.randomUUID(), value: null }];
        this.form.beginnSerie = null;
        this.form.endeSerie = null;
      }
    },
  },
  data() {
    return {
      isLoading: false,
      isSerie: false,
      isDatenUpload: false,
      everyXWeek: 1,
      selectedDays: [],
      dayOptions: [
        { item: 1, name: 'Montag' },
        { item: 2, name: 'Dienstag' },
        { item: 3, name: 'Mittwoch' },
        { item: 4, name: 'Donnerstag' },
        { item: 5, name: 'Freitag' },
        { item: 6, name: 'Samstag' },
        { item: 0, name: 'Sonntag' },
      ],
      dayFormatter: {
        Mo: 'Mo',
        Tu: 'Di',
        Di: 'Tu',
        Mi: 'We',
        Do: 'Th',
        So: 'Su',
        We: 'Mi',
        Th: 'Do',
        Fr: 'Fr',
        Sa: 'Sa',
        Su: 'So',
      },
      form: {
        reisekuerzel: null,
        beginnSerie: null,
        endeSerie: null,
      },
      datesInInterval: [],
      existingDates: [],
      verworfeneDates: {},
      abreisedaten: [{ id: crypto.randomUUID(), value: null }],
      csvFehlermeldung: null,
      restriktionen: [],
    };
  },
  created() {
    this.$_restriktionenAbortController = null;
  },
  beforeDestroy() {
    this.resetRestriktionen();
  },
  validations: {
    form: {
      reisekuerzel: {
        required,
        exactlySixChars,
        noDigits,
      },
      beginnSerie: {
        ifChecked(v) {
          return this.isSerie && required(v);
        },
      },
      endeSerie: {
        ifChecked(v) {
          return this.isSerie && required(v);
        },
      },
    },
  },
  computed: {
    ...mapState({
      reisekuerzelOptions: state => state.reisen.reisenkuerzelFilterOptions,
    }),
    // gesperrte Tage der Reise, wird von allen drei Datepickern genutzt
    restriktionenByDay() {
      return groupRestriktionenByDay(this.restriktionen);
    },
    isTerminAlreadyExistantInSerie() {
      return this.existingDates.some(listDate =>
        this.datesInInterval.some(date => this.areDatesEqual(date, listDate, false))
      );
    },
    isTerminAlreadyExistantSoloTermine() {
      return this.existingDates.some(existingDate =>
        this.abreisedaten
          .filter(item => item.value != null)
          .some(date => this.areDatesEqual(date.value, existingDate, false))
      );
    },
    // mindestens ein eingetragenes Abreisedatum liegt auf einem gesperrten Tag
    istGesperrterTerminVorhandenSolo() {
      return this.abreisedaten.some(item => this.gesperrterTypFuerDatum(item.value) !== null);
    },
    existingDatesTimes() {
      return this.existingDates.map(date => format(date, 'yyyy-MM-dd'));
    },
    reisenOptions() {
      if (this.reisekuerzelOptions.length > 0) {
        return this.reisekuerzelOptions
          .map(reise => reise.reisekuerzel)
          .slice()
          .sort();
      }
      return [];
    },
    beginnSerie() {
      if (this.form.beginnSerie) {
        return this.form.beginnSerie;
      } else return null;
    },
    endeSerie() {
      if (this.form.endeSerie) {
        return this.form.endeSerie;
      } else return null;
    },
    minFaelligkeitsdatum() {
      return new Date();
    },
    disableSichernButton() {
      return (
        this.isLoading ||
        !this.form.reisekuerzel ||
        (!this.isSerie && this.abreisedaten.some(item => item.value == null)) ||
        this.isTerminAlreadyExistantSoloTermine ||
        (!this.isSerie && this.istGesperrterTerminVorhandenSolo) ||
        (this.isSerie && this.formatDatesInInterval.length === 0) ||
        this.isTerminAlreadyExistantInSerie
      );
    },
    getDatesInInterval() {
      if (this.everyXWeek > 0 && this.beginnSerie && this.endeSerie && this.isSerie) {
        let result = [];
        // get all days that are selected in the period
        let iteratedDate = this.beginnSerie;
        while (!isAfter(iteratedDate, this.endeSerie)) {
          if (this.selectedDays.includes(getDay(iteratedDate))) {
            result.push(iteratedDate);
          }
          iteratedDate = addDays(iteratedDate, 1);
        }
        const weekOfSeriesStart = getWeek(this.beginnSerie, { weekStartsOn: 1 });

        // filter result by every x week
        result = result.filter(date => {
          const weekOfDate = getWeek(date, { weekStartsOn: 1 });
          const isWeekInPeriod = (weekOfDate - weekOfSeriesStart) % this.everyXWeek === 0;
          if (isWeekInPeriod) {
            return true;
          } else return false;
        });

        // gesperrte Tage werden nicht als Reisetermin vorgeschlagen
        return result.filter(date => this.gesperrterTypFuerDatum(date) === null);
      } else return [];
    },
    formatDatesInInterval() {
      if (this.getDatesInInterval?.length > 0) {
        return this.datesInInterval.map(date => {
          const formattedDate = format(date, 'EEEEEE., dd.MM.yyyy');
          const germanWeekDays =
            this.dayFormatter[formattedDate.substring(0, 2)] + formattedDate.substring(2);
          return germanWeekDays;
        });
      } else return [];
    },
    lastAbreisedatum() {
      const abreisedatumFiltered = this.abreisedaten.filter(item => item.value);

      if (abreisedatumFiltered.length == 0) return new Date();
      return abreisedatumFiltered.at(-1).value;
    },
  },
  methods: {
    translateDateToNextYear(date) {
      // Get the current year
      const nextYear = getYear(new Date(date)) + 1;

      // Set the date to the current year
      let translatedDate = setYear(date, nextYear);

      // Ensure the weekday matches the original date
      translatedDate = setDay(translatedDate, getDay(date), { weekStartsOn: 1 });

      return translatedDate;
    },
    async loadReiseterminStartdatenToNextYear(year) {
      if (this.form.reisekuerzel) {
        const firstDate = startOfYear(new Date(+year, 0, 1));
        const lastDate = endOfYear(new Date(+year, 0, 1));

        const filter = {
          abreisedatum: { ge: firstDate, le: lastDate },
          'reise/reisekuerzel': { eq: this.form.reisekuerzel },
          isDeleted: false,
        };
        const select = ['abreisedatum', 'state'];
        const orderBy = ['abreisedatum'];

        const query = buildQuery({
          filter,
          select,
          orderBy,
        });

        this.isLoading = true;

        try {
          await apiService.get('Reisetermin' + query).then(res => {
            const { value } = res.data.result;
            if (value.length > 0) {
              this.abreisedaten = value
                .map(item => ({
                  id: crypto.randomUUID(),
                  value: this.translateDateToNextYear(new Date(item.abreisedatum)),
                }))
                .sort((a, b) => compareAsc(new Date(a.value), new Date(b.value)));

              this.verworfeneDates = value.reduce((acc, curr) => {
                const datumNextYear = this.translateDateToNextYear(new Date(curr.abreisedatum));
                const formattedDateNextYear = format(datumNextYear, 'yyyy-MM-dd');
                acc[formattedDateNextYear] = curr.state === 'Verworfen';
                return acc;
              }, {});
            }
          });
        } finally {
          this.isLoading = false;
        }
      }
    },
    areDatesEqual(date1, date2, considerTime = false) {
      if (considerTime) {
        return isEqual(date1, date2);
      } else {
        return isSameDay(date1, date2);
      }
    },
    async loadReisekuerzelInformation(reisekuerzel) {
      this.existingDates = [];
      this.verworfeneDates = {};
      this.abreisedaten = [{ id: crypto.randomUUID(), value: null }];
      this.ladeRestriktionenFuerReise(reisekuerzel);
      this.isLoading = true;

      const filter = {
        abreisedatum: { ge: new Date() },
        'reise/reisekuerzel': reisekuerzel,
        isDeleted: false,
      };

      const select = ['abreisedatum'];
      const query = buildQuery({ filter, select });
      try {
        await apiService.get('Reisetermin' + query).then(res => {
          const { value } = res.data.result;

          this.existingDates = value
            .map(rt => new Date(rt.abreisedatum))
            .sort((a, b) => compareAsc(new Date(a), new Date(b)));
        });
      } finally {
        this.isLoading = false;
      }
    },
    removeAlreadyExistingDatesSerie() {
      this.datesInInterval = this.datesInInterval.filter(
        date => !this.existingDates.some(existingDate => this.areDatesEqual(existingDate, date, false))
      );
    },
    removeAlreadyExistingDatesSolo() {
      const allDates = this.abreisedaten.filter(item => item.value != null).map(item => item.value);
      const datesNotExisting = allDates
        .filter(item => !this.existingDatesTimes.includes(format(new Date(item), 'yyyy-MM-dd')))
        .sort((a, b) => compareAsc(new Date(a), new Date(b)));

      if (datesNotExisting.length > 0) {
        this.abreisedaten = datesNotExisting.map(item => ({
          id: crypto.randomUUID(),
          value: item,
        }));
      } else {
        this.abreisedaten = [{ id: crypto.randomUUID(), value: null }];
      }
    },
    removeGesperrteDatesSolo() {
      const freieDates = this.abreisedaten
        .filter(item => item.value != null && this.gesperrterTypFuerDatum(item.value) === null)
        .map(item => item.value)
        .sort((a, b) => compareAsc(new Date(a), new Date(b)));

      if (freieDates.length > 0) {
        this.abreisedaten = freieDates.map(item => ({
          id: crypto.randomUUID(),
          value: item,
        }));
      } else {
        this.abreisedaten = [{ id: crypto.randomUUID(), value: null }];
      }
    },
    resetRestriktionen() {
      if (this.$_restriktionenAbortController) {
        this.$_restriktionenAbortController.abort();
        this.$_restriktionenAbortController = null;
      }
      this.restriktionen = [];
    },
    // die gesperrten Tage der Reise
    async ladeRestriktionenFuerReise(reisekuerzel) {
      this.resetRestriktionen();
      if (!reisekuerzel) {
        return;
      }

      const controller = new AbortController();
      this.$_restriktionenAbortController = controller;

      const von = moment().startOf('day').format(DAY_KEY_FORMAT);
      const bis = moment().add(3, 'years').endOf('year').format(DAY_KEY_FORMAT);

      try {
        const result = await getRestriktionskonfiguration({
          von,
          bis,
          reisen: [reisekuerzel],
          signal: controller.signal,
        });
        // das Ergebnis eines zwischenzeitlich abgebrochenen Requests wird verworfen
        if (controller.signal.aborted) {
          return;
        }
        this.restriktionen = mapKonfigurationToRestriktionen(result);
      } finally {
        if (this.$_restriktionenAbortController === controller) {
          this.$_restriktionenAbortController = null;
        }
      }
    },
    // Restriktionstyp mit der höchsten Priorität an einem Tag, null wenn der Tag frei ist
    gesperrterTypFuerDatum(datum) {
      if (!datum) {
        return null;
      }
      const entries = this.restriktionenByDay[toDayKey(datum)];
      if (!entries || !entries.length) {
        return null;
      }
      return sortRestriktionenByPriority(entries)[0].type;
    },
    // Statusklasse des Eingabefelds, existierende Termine haben Vorrang vor gesperrten und verworfenen Tagen
    inputStatusKlasse(abreisedatum) {
      if (!abreisedatum) {
        return '';
      }
      if (this.existingDates.some(item => this.areDatesEqual(item, abreisedatum))) {
        return 'existingReisetermin';
      }
      const type = this.gesperrterTypFuerDatum(abreisedatum);
      if (type) {
        return `gesperrterReisetermin gesperrterReisetermin--${type}`;
      }
      return this.isDatumVerworfen(abreisedatum) ? 'verworfenerReisetermin' : '';
    },
    isListMissingOneElement(list1, list2) {
      if (Math.abs(list1.length - list2.length) !== 1) return false;
      const [shorter, longer] = list1.length < list2.length ? [list1, list2] : [list2, list1];
      return longer.filter(item => !shorter.includes(item)).length === 1;
    },
    setDatesInInterval() {
      this.datesInInterval = this.getDatesInInterval;
    },
    addAbreisedatum() {
      this.abreisedaten.push({
        id: crypto.randomUUID(),
        value: null,
      });
    },
    removeAbreisedatum(id) {
      this.abreisedaten = this.abreisedaten.filter(item => item.id != id);
    },
    disabledBeforeToday(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return date < today;
    },
    removeDateFromInterval(dateToDelete) {
      const formattedDateToDelete = new Date(parse(dateToDelete.substring(5), 'dd.MM.yyyy', new Date()));
      this.datesInInterval = this.datesInInterval.filter(
        date => date.getTime() != formattedDateToDelete.getTime()
      );
    },
    async reiseterminAnlegen(hide) {
      this.isLoading = true;
      const params = {
        reisekuerzel: this.form.reisekuerzel,
      };
      if (this.isSerie) {
        if (this.datesInInterval.length === 0) {
          this.$bvToast.toast('Falsche Einstellungen der Serie, kein Datum gefunden.', {
            title: 'Error',
            variant: 'danger',
          });

          return;
        }
        params.abreisedatum = this.datesInInterval.map(date => format(date, 'yyyy-MM-dd'));
      } else {
        if (this.abreisedaten.length > 0) {
          params.abreisedatum = [...this.abreisedaten.map(item => format(item.value, 'yyyy-MM-dd'))];
        }
      }
      apiService
        .post('Reisetermin', params)
        .then(() => {
          this.toast('Erfolgreich erstellt.');
          this.isLoading = false;
          hide();
          this.$emit('reiseterminAnlegen');
        })
        .catch(() => {
          this.isLoading = false;
          hide();
        });
    },
    onShow() {
      this.$v.form.$touch();
    },
    onHide() {
      this.form = {
        reisekuerzel: null,
        beginnSerie: null,
        endeSerie: null,
      };
      this.abreisedaten = [{ id: crypto.randomUUID(), value: null }];
      this.isSerie = false;
      this.isDatenUpload = false;
      this.everyXWeek = 1;
      this.selectedDays = [];
      this.verworfeneDates = {};
      this.existingDates = [];
      this.csvFehlermeldung = null;
      this.resetRestriktionen();
    },
    uploadCSV(event) {
      const file = event.target.files[0] || event;

      if (!file) {
        console.log('Keine Datei hochgeladen');
        return;
      }

      const reader = new FileReader();
      reader.onload = e => {
        const csv = e.target.result;
        this.leseCSVUndSetzeTermine(csv);
        this.$refs.formFileUpload.reset();
      };
      reader.readAsText(file);
    },
    leseCSVUndSetzeTermine(csvText) {
      try {
        this.csvFehlermeldung = null;

        const cleanCsv = csvText.replace(/^\uFEFF/, '');
        const zeilen = cleanCsv.trim().split('\n');

        if (zeilen.length < 2) {
          console.log('Es ist nur die Überschrift vorhanden und deswege zu wenige Zeilen');
          return;
        }

        const header = zeilen[0].split(';').map(h => h.trim());
        if (header.length !== 2 || header[0] !== 'Reisekuerzel' || header[1] !== 'Abreisedatum') {
          this.csvFehlermeldung = 'Fehler im Datenformat: Bitte überprüfe die Überschriften der CSV-Datei.';
          return;
        }

        // Header wird übersprungen
        const datenZeile = zeilen.slice(1);
        const termine = [];
        let reisekuerzel = null;

        for (const zeile of datenZeile) {
          const [kuerzel, datum] = zeile.split(';');

          if (kuerzel && datum) {
            const kuerzelTrimmed = kuerzel.trim();

            if (!/^[A-Z]{6}$/.test(kuerzelTrimmed)) {
              this.csvFehlermeldung = 'Fehler im Datenformat: Bitte überprüfe das Reisekürzel der CSV-Datei.';
              return;
            }

            if (!reisekuerzel) {
              reisekuerzel = kuerzelTrimmed;
            }

            const datumTrimmed = datum.trim();
            let dateObj;

            // Validierung: Datum muss DD.MM.YYYY oder DD.MM.YY Format haben
            if (/^\d{2}\.\d{2}\.\d{4}$/.test(datumTrimmed)) {
              // Versucht DD.MM.YYYY Format zu parsen (z.B. 01.08.2025) <- (vollständiges Datum)
              dateObj = parse(datumTrimmed, 'dd.MM.yyyy', new Date());
            } else if (/^\d{2}\.\d{2}\.\d{2}$/.test(datumTrimmed)) {
              // Versucht DD.MM.YY Format zu parsen (z.B. 01.08.25) <- Excel zeigt das so in der Zelle an (kurze Datumsanzeige)
              dateObj = parse(datumTrimmed, 'dd.MM.yy', new Date());
            } else {
              // Ungültiges Datumsformat
              this.csvFehlermeldung =
                'Fehler im Datenformat: Bitte überprüfe das Abreisedatum der CSV-Datei.';
              return;
            }

            if (!isNaN(dateObj)) {
              termine.push({
                id: crypto.randomUUID(),
                value: dateObj,
              });
            }
          }
        }

        if (termine.length > 0 && reisekuerzel) {
          this.form.reisekuerzel = reisekuerzel;

          this.loadReisekuerzelInformation(reisekuerzel).then(() => {
            this.abreisedaten = termine.sort((a, b) => compareAsc(new Date(a.value), new Date(b.value)));
            this.isDatenUpload = false;
            this.isSerie = false;
          });
        } else {
          console.log('FEHLER: Keine Termine oder kein Reisekürzel gefunden!');
        }
      } catch (error) {
        console.error('Fehler beim laden der CSV:', error);
        this.$bvToast.toast('Fehler beim Verarbeiten der CSV-Datei.', {
          title: 'Fehler',
          variant: 'danger',
        });
      }
    },
    isFormattedDateInExistingDates(formattedDate) {
      const revertGermanWeekDays =
        this.dayFormatter[formattedDate.substring(0, 2)] + formattedDate.substring(2);
      const date = parse(revertGermanWeekDays, 'EEEEEE., dd.MM.yyyy', new Date());
      return this.existingDates.some(listDate => this.areDatesEqual(date, listDate, false));
    },
    isDatumVerworfen(abreisedatum) {
      if (!abreisedatum) return false;
      const formattedDate = format(abreisedatum, 'yyyy-MM-dd');
      if (!Object.keys(this.verworfeneDates).includes(formattedDate)) return false;
      console.log(this.verworfeneDates[formattedDate]);
      return this.verworfeneDates[formattedDate];
    },
  },
};
</script>

<style scoped>
.centerModal {
  display: flex;
  justify-content: center;
  align-items: center;
}
:deep(.existingReisetermin .mx-input-wrapper > input) {
  background-color: #f64e6025;
}

:deep(.verworfenerReisetermin .mx-input-wrapper > input) {
  background-color: #ffa80050;
}

:deep(.gesperrterReisetermin--keine-abreise-hart .mx-input-wrapper > input) {
  background-color: var(--restriktion-keine-abreise-hart-bg);
}

:deep(.gesperrterReisetermin--keine-abreise .mx-input-wrapper > input) {
  background-color: var(--restriktion-keine-abreise-bg);
}

:deep(.gesperrterReisetermin--keine-reisen .mx-input-wrapper > input) {
  background-color: var(--restriktion-keine-reisen-bg);
}

.plus-sign {
  height: 18px;
  width: 18px;
  border-radius: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  color: white;
  cursor: pointer;
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
}

div.existingReisetermin {
  background-color: #f64e6075;
  border-radius: 4px;
}

.bgLightGray {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
}
:deep(.mx-datepicker-content) {
  margin: 0 !important;
}
:deep(.modal-dialog) {
  max-width: 560px !important;
}
</style>
